import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// KTK-386 — daily spend-over-time column chart for the spend dashboard.
//
// Single-series, sequential: every column wears --chart-mark (validated
// per theme mode against the paper-2 surface); identity comes from the
// title, so there is no legend. Marks follow the house chart specs:
// columns ≤ 24px with a 2px surface gap, 4px rounded data-end at the top
// and square at the baseline, hairline solid gridlines, selective direct
// labels (peak day only — the tooltip and y-axis carry the rest). Hover
// and keyboard focus share one tooltip; the hit target is the full
// column slot, not the painted mark.
import { useMemo, useState } from "react";
const W = 640;
const H = 170;
const PAD_L = 44; // y tick labels
const PAD_R = 8;
const PAD_T = 18; // headroom for the peak label
const PAD_B = 20; // x tick labels
export function SpendOverTimeChart({ days }) {
    const [hover, setHover] = useState(null);
    const { bars, ticks, peakIdx } = useMemo(() => {
        const sorted = [...days].sort((a, b) => (a.day < b.day ? -1 : 1));
        const max = Math.max(...sorted.map((d) => d.cost_usd), 0);
        const niceMax = niceCeil(max);
        const plotW = W - PAD_L - PAD_R;
        const plotH = H - PAD_T - PAD_B;
        const n = Math.max(sorted.length, 1);
        const slotW = plotW / n;
        const barW = Math.min(24, Math.max(3, slotW - 2));
        let peak = 0;
        sorted.forEach((d, i) => {
            if (d.cost_usd > (sorted[peak]?.cost_usd ?? 0))
                peak = i;
        });
        return {
            peakIdx: peak,
            bars: sorted.map((d, i) => {
                const h = niceMax > 0 ? (d.cost_usd / niceMax) * plotH : 0;
                return {
                    d,
                    x: PAD_L + i * slotW + (slotW - barW) / 2,
                    slotX: PAD_L + i * slotW,
                    slotW,
                    w: barW,
                    y: H - PAD_B - h,
                    h,
                };
            }),
            ticks: [0, niceMax / 2, niceMax].map((v) => ({
                v,
                y: H - PAD_B - (niceMax > 0 ? (v / niceMax) * plotH : 0),
            })),
        };
    }, [days]);
    if (days.length === 0) {
        return (_jsx("div", { style: { padding: 16, fontSize: "var(--fs-meta)", color: "var(--ink-3)" }, children: "No spend recorded in this range." }));
    }
    const hovered = hover != null ? bars[hover] : null;
    return (_jsxs("div", { style: { position: "relative" }, children: [_jsxs("svg", { viewBox: `0 0 ${W} ${H}`, style: { width: "100%", height: "auto", display: "block" }, role: "img", "aria-label": "Daily spend", onPointerLeave: () => setHover(null), children: [ticks.map((t) => (_jsxs("g", { children: [_jsx("line", { x1: PAD_L, x2: W - PAD_R, y1: t.y, y2: t.y, stroke: "var(--hairline-2)", strokeWidth: 1 }), _jsx("text", { x: PAD_L - 6, y: t.y + 3, textAnchor: "end", fontSize: 9, fill: "var(--ink-3)", style: { fontVariantNumeric: "tabular-nums" }, children: formatUsd(t.v) })] }, t.v))), bars.map((b, i) => (_jsx("path", { d: columnPath(b.x, b.y, b.w, b.h), fill: "var(--chart-mark)", opacity: hover == null || hover === i ? 1 : 0.45 }, b.d.day))), bars.map((b, i) => {
                        const show = i === 0 || i === bars.length - 1 || i % 7 === 0;
                        if (!show || (i !== bars.length - 1 && bars.length - 1 - i < 4 && i !== 0))
                            return null;
                        return (_jsx("text", { x: b.slotX + b.slotW / 2, y: H - 6, textAnchor: "middle", fontSize: 9, fill: "var(--ink-3)", children: b.d.day.slice(5) }, `x-${b.d.day}`));
                    }), (() => {
                        const peak = bars[peakIdx];
                        if (!peak || peak.h <= 0)
                            return null;
                        return (_jsx("text", { x: Math.min(Math.max(peak.x + peak.w / 2, PAD_L + 16), W - PAD_R - 16), y: peak.y - 5, textAnchor: "middle", fontSize: 10, fontWeight: 600, fill: "var(--ink-2)", style: { fontVariantNumeric: "tabular-nums" }, children: formatUsd(peak.d.cost_usd) }));
                    })(), bars.map((b, i) => (_jsx("rect", { x: b.slotX, y: PAD_T, width: b.slotW, height: H - PAD_T - PAD_B, fill: "transparent", tabIndex: 0, "aria-label": `${b.d.day}: ${formatUsd(b.d.cost_usd)}, ${b.d.turns} turns`, onPointerEnter: () => setHover(i), onFocus: () => setHover(i), onBlur: () => setHover(null), style: { outline: "none" } }, `hit-${b.d.day}`)))] }), hovered && (_jsxs("div", { style: {
                    position: "absolute",
                    left: `${((hovered.slotX + hovered.slotW / 2) / W) * 100}%`,
                    top: 0,
                    transform: `translateX(${hovered.slotX > W * 0.7 ? "-100%" : "-50%"})`,
                    pointerEvents: "none",
                    background: "var(--paper)",
                    border: "1px solid var(--hairline)",
                    borderRadius: "var(--radius-sm)",
                    padding: "5px 9px",
                    fontSize: 11,
                    color: "var(--ink)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    whiteSpace: "nowrap",
                }, children: [_jsx("span", { style: { fontWeight: 600, fontVariantNumeric: "tabular-nums" }, children: formatUsd(hovered.d.cost_usd) }), " ", _jsxs("span", { style: { color: "var(--ink-3)" }, children: ["\u00B7 ", hovered.d.turns, " turn", hovered.d.turns === 1 ? "" : "s", " \u00B7 ", hovered.d.day] })] }))] }));
}
/** Column with a 4px rounded data-end (top) and a square baseline. */
function columnPath(x, y, w, h) {
    if (h <= 0)
        return "";
    const r = Math.min(4, w / 2, h);
    const bottom = y + h;
    return [
        `M${x},${bottom}`,
        `V${y + r}`,
        `Q${x},${y} ${x + r},${y}`,
        `H${x + w - r}`,
        `Q${x + w},${y} ${x + w},${y + r}`,
        `V${bottom}`,
        "Z",
    ].join("");
}
/** Round a max up to a clean tick value (1/2/5 × 10ⁿ). */
function niceCeil(v) {
    if (v <= 0)
        return 0;
    const exp = Math.floor(Math.log10(v));
    const base = 10 ** exp;
    for (const m of [1, 2, 5, 10]) {
        if (v <= m * base)
            return m * base;
    }
    return 10 * base;
}
function formatUsd(v) {
    if (v >= 100)
        return `$${Math.round(v).toLocaleString()}`;
    if (v >= 1)
        return `$${v.toFixed(2)}`;
    return `$${v.toFixed(3)}`;
}
//# sourceMappingURL=SpendOverTimeChart.js.map